import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {message} from 'antd';
import {AxiosError, AxiosResponse} from 'axios';
import type {ADD_STATION, STATION_DATA, UPDATE_STATION} from './stations.type';
import type FILTERS from '../../../../common/filters.type';

import stationsService from './stationsServices';

const initialState = {
  isLoading: false,
  stations: {
    page_size: 10,
    page_number: 1,
    sort_by: 'name',
    ascending: true,
    search: '',
    totalDataCount: 0,
    is_active: undefined as boolean | undefined,
    pageData: [] as STATION_DATA[],
  },
};

export const stationsList = createAsyncThunk('stationsList', async (payload: FILTERS, thunkAPI) => {
  try {
    const data: AxiosResponse = await stationsService.stationsList(payload);

    return data;
  } catch (error) {
    const result = error as AxiosError;

    return thunkAPI.rejectWithValue(result.message);
  }
});
export const authStationsList = createAsyncThunk(
  'authStationsList',
  async (payload: FILTERS, thunkAPI) => {
    try {
      const data: AxiosResponse = await stationsService.authStationsList(payload);

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const addStation = createAsyncThunk('addStation', async (values: ADD_STATION, thunkAPI) => {
  try {
    const data: AxiosResponse = await stationsService.addStation(values);

    return data;
  } catch (error) {
    const result = error as AxiosError;

    return thunkAPI.rejectWithValue(result.message);
  }
});
export const updateStation = createAsyncThunk(
  'updateStation',
  async (
    values: {
      payload: UPDATE_STATION;
      id: number;
    },
    thunkAPI,
  ) => {
    try {
      const data: AxiosResponse = await stationsService.updateStation({
        payload: values.payload,
        id: values.id,
      });

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const stationsSlice = createSlice({
  name: 'stations',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    pageNumberChange(state, action: PayloadAction<number>) {
      state.stations.page_number = action.payload;
    },
    pageSizeChange(state, action: PayloadAction<number>) {
      state.stations.page_number = 1;
      state.stations.page_size = action.payload;
    },
    searchBy(state, action: PayloadAction<string>) {
      state.stations.page_number = 1;
      state.stations.search = action.payload;
    },
    stationsStatusFilter(state, action: PayloadAction<boolean | undefined>) {
      state.stations.page_number = 1;
      state.stations.is_active = action.payload;
    },
    clearStationsFilters(state) {
      state.stations.page_number = 1;
      state.stations.page_size = 10;
      state.stations.search = '';
      state.stations.sort_by = 'name';
      state.stations.is_active = undefined;
    },
    sortBy(state, action: PayloadAction<{ascending: boolean; sortBy: string}>) {
      state.stations.ascending = action.payload.ascending;
      state.stations.sort_by = action.payload.sortBy;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(stationsList.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(stationsList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.stations = action.payload.data;
    });
    builder.addCase(stationsList.rejected, (state, action) => {
      state.isLoading = false;
      state.stations = initialState.stations;
      message.error(`${action.payload}`);
    });
    builder.addCase(authStationsList.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(authStationsList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.stations = action.payload.data;
    });
    builder.addCase(authStationsList.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });

    builder.addCase(addStation.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(addStation.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Station added Successfully`);
    });
    builder.addCase(addStation.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });

    builder.addCase(updateStation.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateStation.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Station updated Successfully`);
    });
    builder.addCase(updateStation.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });
  },
});
export const {
  pageNumberChange,
  pageSizeChange,
  searchBy,
  sortBy,
  clearStationsFilters,
  stationsStatusFilter,
} = stationsSlice.actions;

export default stationsSlice.reducer;
